import { escapeHtml } from '../utils/security.js';
import { listAppFactorySessions, deleteAppFactorySession } from '../api.js';
import { getCurrentUser } from '../auth.js';

const markup = String.raw;

const formatSessionLabel = (session) => {
  const base = session?.displayName
    ? escapeHtml(session.displayName)
    : `Session ${escapeHtml(session.sessionId?.substring(0, 8) ?? '')}`;
  const meta = [];
  if (session?.customerId) meta.push(`Kunde: ${escapeHtml(session.customerId)}`);
  if (session?.projectId) meta.push(`Projekt: ${escapeHtml(session.projectId)}`);
  const status = session?.status ? escapeHtml(session.status) : '';
  return [base, meta.join(' · '), status].filter(Boolean).join(' · ');
};

const renderSessions = (sessions, user) => {
  if (!sessions.length) {
    return markup`
      <div class="empty-state">
        <p class="muted">Noch keine Sessions vorhanden.</p>
        <p class="muted small">
          Starte eine neue Session, um ein strukturiertes Kundengespräch zu führen.
        </p>
      </div>
    `;
  }

  return markup`
    <div class="sessions-grid">
      ${sessions
        .map(
          (session) => markup`
            <div class="session-card" data-session-id="${escapeHtml(session.sessionId)}">
              <div class="session-header">
                <h3>${formatSessionLabel(session)}</h3>
                <span class="session-status status-${escapeHtml(session.status ?? '')}">${escapeHtml(session.status ?? '')}</span>
              </div>
              <div class="session-body">
                ${user?.role === 'admin' ? `<p class="muted small">Owner: ${escapeHtml(session.ownerEmail ?? '')}</p>` : ''}
                <div class="progress-info">
                  <div class="progress-bar">
                    <div
                      class="progress-fill"
                      style="width: ${session.progress?.percentComplete ?? 0}%"
                    ></div>
                  </div>
                  <p class="muted small">
                    ${session.progress?.completedCategories ?? 0} / ${session.progress?.totalCategories ?? 0}
                    Kategorien abgeschlossen (${session.progress?.percentComplete ?? 0}%)
                  </p>
                </div>
                <p class="muted tiny">
                  Erstellt: ${session.createdAt ? new Date(session.createdAt).toLocaleString('de-DE') : '-'}
                </p>
              </div>
              <div class="session-actions">
                <button type="button" class="view-session-btn ghost-button" data-id="${escapeHtml(session.sessionId)}">
                  Öffnen
                </button>
                ${
                  session.status === 'in_progress'
                    ? `<button type="button" class="continue-session-btn" data-id="${escapeHtml(session.sessionId)}">Fortsetzen</button>`
                    : ''
                }
                <button type="button" class="delete-session-btn ghost-button" data-id="${escapeHtml(session.sessionId)}">Löschen</button>
              </div>
            </div>
          `,
        )
        .join('')}
    </div>
  `;
};

export const renderAppFactoryDashboardView = async (container) => {
  const user = getCurrentUser();

  container.innerHTML = markup`
    <div class="container">
      <div class="page-header">
        <h1>App Factory</h1>
        <p class="muted">Erstelle Apps aus strukturierten Kundengesprächen</p>
      </div>

      <div id="sessions-container">
        <div class="loading-spinner">Lade Sessions...</div>
      </div>

      <div style="margin-top: 2rem;">
        <button type="button" id="new-session-btn" class="primary-button">
          Neue App Factory Session starten
        </button>
      </div>
    </div>
  `;

  const sessionsContainer = document.getElementById('sessions-container');
  const newSessionBtn = document.getElementById('new-session-btn');

  const loadSessions = async () => {
    try {
      const response = await listAppFactorySessions();
      const sessions = response?.sessions ?? [];
      sessionsContainer.innerHTML = renderSessions(sessions, user);

      sessionsContainer.querySelectorAll('.view-session-btn').forEach((btn) => {
        btn.addEventListener('click', (event) => {
          const sessionId = event.currentTarget.dataset.id;
          window.location.hash = `#/app-factory/review/${sessionId}`;
        });
      });

      sessionsContainer.querySelectorAll('.continue-session-btn').forEach((btn) => {
        btn.addEventListener('click', (event) => {
          const sessionId = event.currentTarget.dataset.id;
          window.location.hash = `#/app-factory/session/${sessionId}`;
        });
      });

      sessionsContainer.querySelectorAll('.delete-session-btn').forEach((btn) => {
        btn.addEventListener('click', async (event) => {
          const sessionId = event.currentTarget.dataset.id;
          const confirmDelete = window.confirm('Session wirklich löschen? (Conversation & Anforderungen werden entfernt)');
          if (!confirmDelete) return;
          try {
            btn.disabled = true;
            await deleteAppFactorySession(sessionId);
            await loadSessions();
          } catch (error) {
            console.error('Failed to delete session', error);
            alert('Session konnte nicht gelöscht werden.');
          } finally {
            btn.disabled = false;
          }
        });
      });
    } catch (error) {
      console.error('Failed to load sessions', error);
      sessionsContainer.innerHTML = '<p class="muted">Sessions konnten nicht geladen werden.</p>';
    }
  };

  newSessionBtn?.addEventListener('click', () => {
    window.location.hash = '#/app-factory/new';
  });

  loadSessions();
};
